import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCartSimple,
  MagnifyingGlass,
  List,
  X,
  UserCircle,
  SignOut,
  SquaresFour,
  Package,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "About", to: "/#about" },
  { label: "Contact", to: "/contact" },
];

function UserMenu({ user, logout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="account-menu-trigger"
          className="flex items-center gap-2 border border-cyan/15 px-3 py-2 text-teal-muted transition-colors hover:border-cyan/40 hover:text-cyan"
        >
          <UserCircle size={20} weight="duotone" />
          <span className="hidden max-w-[100px] truncate font-mono text-xs uppercase tracking-wider sm:inline">
            {user.name?.split(" ")[0] || "Account"}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 border-cyan/20 bg-navy-surface text-white">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-teal">
          {user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-cyan/15" />
        <DropdownMenuItem asChild>
          <Link to="/account" data-testid="menu-account" className="cursor-pointer gap-2 focus:bg-cyan/10 focus:text-cyan">
            <Package size={16} /> My Orders
          </Link>
        </DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem asChild>
            <Link to="/admin" data-testid="menu-admin" className="cursor-pointer gap-2 focus:bg-cyan/10 focus:text-cyan">
              <SquaresFour size={16} /> Admin Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-cyan/15" />
        <DropdownMenuItem
          data-testid="menu-logout"
          onClick={logout}
          className="cursor-pointer gap-2 text-teal-muted focus:bg-destructive/15 focus:text-destructive"
        >
          <SignOut size={16} /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNav({ open, query, setQuery, onSearch, user, onClose }) {
  if (!open) return null;
  return (
    <div className="border-t border-cyan/15 bg-navy-surface px-6 py-4 lg:hidden" data-testid="mobile-menu">
      <form onSubmit={onSearch} className="mb-4">
        <input
          data-testid="mobile-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search peptides…"
          className="w-full border border-cyan/15 bg-navy py-2 px-3 font-mono text-xs text-white placeholder:text-teal/60 outline-none focus:border-cyan/50"
        />
      </form>
      <div className="flex flex-col gap-1">
        {NAV_LINKS.map((l) => (
          <Link
            key={l.label}
            to={l.to}
            onClick={onClose}
            className="py-2 font-mono text-xs uppercase tracking-widest text-teal-muted hover:text-cyan"
          >
            {l.label}
          </Link>
        ))}
        {!user && (
          <Link
            to="/login"
            onClick={onClose}
            className="mt-2 border border-cyan/30 py-2 text-center font-mono text-xs uppercase tracking-wider text-cyan"
          >
            Sign In
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-cyan/15 bg-navy/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-6 sm:px-8 lg:px-12">
        <Logo />

        <nav className="ml-6 hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-widest text-teal-muted transition-colors hover:text-cyan"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={onSearch} className="ml-auto hidden flex-1 max-w-xs md:block">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-teal"
            />
            <input
              data-testid="navbar-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search peptides…"
              className="w-full border border-cyan/15 bg-navy-surface py-2 pl-9 pr-3 font-mono text-xs text-white placeholder:text-teal/60 outline-none transition-colors focus:border-cyan/50"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-2">
          {/* Account */}
          {user ? (
            <UserMenu user={user} logout={logout} />
          ) : (
            <Link
              to="/login"
              data-testid="navbar-login-link"
              className="hidden border border-cyan/30 px-4 py-2 font-mono text-xs uppercase tracking-wider text-cyan transition-all duration-300 hover:bg-cyan/10 sm:block"
            >
              Sign In
            </Link>
          )}

          {/* Cart */}
          <Link
            to="/cart"
            data-testid="navbar-cart-link"
            className="relative flex h-10 w-10 items-center justify-center border border-cyan/15 text-teal-muted transition-colors hover:border-cyan/40 hover:text-cyan"
          >
            <ShoppingCartSimple size={20} weight="duotone" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center bg-cyan px-1 font-mono text-[10px] font-bold text-navy-deep"
              >
                {count}
              </span>
            )}
          </Link>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center border border-cyan/15 text-teal-muted lg:hidden"
          >
            {mobileOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <MobileNav
        open={mobileOpen}
        query={query}
        setQuery={setQuery}
        onSearch={onSearch}
        user={user}
        onClose={() => setMobileOpen(false)}
      />
    </header>
  );
}
